// 모든 API 요청에 공통적으로 사용할 응답 처리기
// openapi-fetch 응답 구조에 맞춰 개선된 버전

/**
 * openapi-fetch 라이브러리의 실제 FetchResponse 타입과 호환되는 인터페이스
 * 성공과 실패를 Union Type으로 정확히 표현
 */
export type OpenAPIFetchResponse<T = any> =
	| {
			/** 성공 시 응답 데이터 */
			data: T;
			/** 에러 없음 */
			error?: never;
			/** HTTP Response 객체 */
			response: Response;
	  }
	| {
			/** 실패 시 데이터 없음 */
			data?: never;
			/** 에러 발생 시 에러 정보 */
			error: unknown;
			/** HTTP Response 객체 */
			response: Response;
	  };

/**
 * 로그 레벨 타입
 */
export type LogLevel = 'none' | 'error' | 'warn' | 'info' | 'debug';

/**
 * API 응답 핸들러 옵션
 */
export interface APIResponseOptions {
	/** 에러 발생 시 throw 여부 (기본값: true) */
	throwOnError?: boolean;
	/** 응답에 포함할 헤더 이름 배열 */
	includeHeaders?: string[];
	/** 로깅 활성화 여부 (기본값: true) */
	enableLogging?: boolean;
	/** 로그 레벨 (기본값: 'error') */
	logLevel?: LogLevel;
	/** API 이름 (로깅에 표시, 기본값: 'API') */
	apiName?: string;
}

/**
 * 🎯 개선된 API 응답 핸들러
 * openapi-fetch의 실제 FetchResponse 타입과 완전히 호환
 *
 * @param responsePromise openapi-fetch에서 반환하는 Promise
 * @param options 옵션 객체
 * @returns 성공 시 data, 실패 시 Error throw 또는 error 반환
 */
export async function handleAPIResponse<T>(
	responsePromise: Promise<any>,
	options: APIResponseOptions = {}
): Promise<T> {
	const { throwOnError = true, includeHeaders, enableLogging = true, logLevel = 'error', apiName = 'API' } = options;

	/**
	 * 로그를 출력할지 확인하는 헬퍼 함수
	 */
	const shouldLog = (level: LogLevel): boolean => {
		if (!enableLogging) return false;
		const levels: LogLevel[] = ['none', 'error', 'warn', 'info', 'debug'];
		return levels.indexOf(level) <= levels.indexOf(logLevel);
	};
	try {
		if (shouldLog('debug')) {
			console.log(`[${apiName}] Request started`);
		}

		const result = await responsePromise;

		// 에러가 있는 경우 (openapi-fetch 응답 구조)
		if ('error' in result && result.error) {
			// error는 unknown 타입이므로 안전하게 처리
			let errorMessage = 'API request failed';
			let errorCode: string | undefined;

			if (typeof result.error === 'object' && result.error !== null) {
				// error 객체에서 message와 code 추출 시도
				const errorObj = result.error as any;

				// code 추출
				if (typeof errorObj.code === 'string') {
					errorCode = errorObj.code;
				} else if (typeof errorObj.error === 'string') {
					errorCode = errorObj.error;
				}

				// message 추출
				if (typeof errorObj.message === 'string') {
					errorMessage = errorObj.message;
				} else if (typeof errorObj.detail === 'string') {
					errorMessage = errorObj.detail;
				} else if (typeof errorObj.title === 'string') {
					errorMessage = errorObj.title;
				}
			} else if (typeof result.error === 'string') {
				errorMessage = result.error;
			}

			// HTTP 상태 코드도 포함
			if (result.response?.status) {
				errorMessage += ` (HTTP ${result.response.status})`;
			}

			// 커스텀 에러 객체 생성 (code 정보 포함)
			const error = new Error(errorMessage) as Error & {
				code?: string;
				error?: any;
				response?: Response;
			};

			if (errorCode) {
				error.code = errorCode;
			}

			// 원본 에러 객체도 포함
			error.error = result.error;
			error.response = result.response;

			if (shouldLog('error')) {
				console.error(`[${apiName}] Error:`, errorMessage);
			}

			// throwOnError 옵션에 따라 에러 throw 또는 반환
			if (throwOnError) {
				throw error;
			} else {
				if (shouldLog('warn')) {
					console.warn(`[${apiName}] Error suppressed (throwOnError=false)`);
				}
				// throw하지 않고 result를 그대로 반환 (error와 response 포함)
				return result as T;
			}
		}

		// 데이터가 있는 경우 (openapi-fetch 응답 구조)
		if ('data' in result && result.data !== undefined) {
			if (shouldLog('debug')) {
				console.log(`[${apiName}] Success:`, result.data);
			}

			// includeHeaders가 배열로 전달되면 해당 헤더들을 추출
			if (includeHeaders && Array.isArray(includeHeaders) && includeHeaders.length > 0 && result.response) {
				const headers = result.response.headers;
				const extractedHeaders: Record<string, string | null> = {};

				// 설정된 헤더들만 추출
				includeHeaders.forEach((headerName) => {
					extractedHeaders[headerName] = headers.get(headerName);
				});

				// 헤더 중 하나라도 값이 있으면 _headers 포함
				const hasAnyHeader = Object.values(extractedHeaders).some((value) => value !== null);

				if (hasAnyHeader) {
					if (shouldLog('debug')) {
						console.log(`[${apiName}] Extracted headers:`, extractedHeaders);
					}
					return {
						data: result.data,
						_headers: extractedHeaders,
					} as T;
				}
			}

			return result.data as T;
		}

		// HTTP 상태 코드 기반 에러 처리
		if (result.response && !result.response.ok) {
			const errorMessage = `HTTP ${result.response.status}: ${result.response.statusText}`;
			const error = new Error(errorMessage);

			if (shouldLog('error')) {
				console.error(`[${apiName}] HTTP Error:`, errorMessage);
			}

			if (throwOnError) {
				throw error;
			} else {
				if (shouldLog('warn')) {
					console.warn(`[${apiName}] HTTP Error suppressed (throwOnError=false)`);
				}
				return result as T;
			}
		}

		// 예상치 못한 응답 구조
		const error = new Error('No data received from API');

		if (shouldLog('error')) {
			console.error(`[${apiName}] Unexpected response structure`);
		}

		if (throwOnError) {
			throw error;
		} else {
			if (shouldLog('warn')) {
				console.warn(`[${apiName}] Unexpected response suppressed (throwOnError=false)`);
			}
			return result as T;
		}
	} catch (error) {
		// 네트워크 에러나 기타 예외 처리
		if (shouldLog('error')) {
			console.error(`[${apiName}] Exception:`, error);
		}

		if (throwOnError) {
			if (error instanceof Error) {
				throw error;
			}
			throw new Error(`Unexpected error: ${String(error)}`);
		} else {
			// throwOnError가 false인 경우에는 에러를 반환 형태로 변환
			return {
				error: error instanceof Error ? error : new Error(String(error)),
				response: undefined,
			} as T;
		}
	}
}

/**
 * 레거시 호환성을 위한 핸들러 (deprecated)
 * @deprecated handleAPIResponse 사용 권장
 */
export async function handleAPIResponseLegacy<T>(
	response: Promise<OpenAPIFetchResponse<T>> | OpenAPIFetchResponse<T>
): Promise<T> {
	const resolvedResponse = await Promise.resolve(response);

	if ('error' in resolvedResponse) {
		throw new Error(resolvedResponse.error as string);
	}

	if ('data' in resolvedResponse) {
		return resolvedResponse.data;
	}

	throw new Error('Unexpected response structure');
}

/**
 * 쿼리 파라미터를 생성하는 유틸리티 함수
 * 기본값과 사용자 입력을 병합하여 완전한 쿼리 파라미터 객체 생성
 *
 * @param defaultParams 기본 파라미터 (page, limit 등)
 * @param overrideParams 사용자가 제공한 파라미터
 * @returns 병합된 파라미터 객체
 */
export function createQueryParams<T extends Record<string, any>>(defaultParams: T, overrideParams?: Partial<T>): T {
	return { ...defaultParams, ...overrideParams };
}

/**
 * 🎯 타입 안전한 쿼리 파라미터 생성 함수
 * undefined 값을 자동으로 제거하여 깔끔한 쿼리 생성
 */
export function createCleanQueryParams<T extends Record<string, any>>(params: T): Partial<T> {
	const cleanParams: Partial<T> = {};

	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			cleanParams[key as keyof T] = value;
		}
	});

	return cleanParams;
}
